/**
 * UDT-instance folder-level browse extractors and mapper.
 *
 *  - `extractUdtDirectMembers(value, context)` projects the
 *    `directMembers` CALL output (real runtime `:Tag` HAS_MEMBER rows)
 *    into the unified {@link PlantProviderTag} shape so the existing
 *    `TagRow` / `TagTable` UI can render them uniformly.
 *  - `extractUdtNestedInstanceEntries(value)` projects the
 *    `directNestedInstances` CALL output into typed
 *    {@link PlantUdtNestedInstanceEntry}s for the folder-row links
 *    that drill into nested instances.
 *  - `mapPlantUdtFolderNodeResponse(raw, args)` projects the single
 *    row returned by `PLANT_UDT_FOLDER_NODE_CYPHER` into the full
 *    {@link PlantUdtFolderNodeResponse} bundle.
 *
 * Pure functions, no RTK / no Neo4j driver — trivially unit-testable.
 */

import { extractRawNode, leafTagSegment, toIntCount } from '../graph-row'
import { extractSubfolderEntries } from './provider-folder-node'
import type {
  CypherReadResult,
  PlantProviderTag,
  PlantUdtFolderNodeArgs,
  PlantUdtFolderNodeResponse,
  PlantUdtNestedInstanceEntry
} from '../types'

/**
 * Project the `{member, leafName}` rows from
 * `PLANT_UDT_FOLDER_NODE_CYPHER`'s `directMembers` CALL into
 * {@link PlantProviderTag} rows so the existing `TagRow` / `TagTable`
 * UI can render them uniformly. `member` is the actual runtime `:Tag`
 * HAS_MEMBER from the current `:UDTInstance`, so `tag_path` is already
 * in Ignition's `[Provider]Folder/Tag` form on the node and no
 * client-side composition is needed.
 *
 * `ignition_tag_type` is forced to `'UDTMember'` (vs the node's
 * `ignition_tag_type='udt_member'`) so the UI can paint the small
 * "UDT" pill on these rows; the underlying `member_type` (`opc` /
 * `memory` / `expression` / …) survives in its own column.
 *
 * `context.providerName` / `context.instanceFullPath` are accepted for
 * backwards-compat with callers that pre-date the tag_path-on-node
 * refactor — they're no longer needed for path composition.
 */
export function extractUdtDirectMembers(
  value: unknown,
  context: { providerName?: string; instanceFullPath?: string }
): PlantProviderTag[] {
  if (!Array.isArray(value)) return []
  const out: PlantProviderTag[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const memberRaw = extractRawNode(obj['member'])
    if (!memberRaw) continue
    const id =
      typeof memberRaw['id'] === 'string'
        ? (memberRaw['id'] as string)
        : typeof memberRaw['_id'] === 'string'
          ? (memberRaw['_id'] as string)
          : null
    if (!id) continue
    if (seen.has(id)) continue
    seen.add(id)
    const elementId = typeof memberRaw['_id'] === 'string' ? (memberRaw['_id'] as string) : id
    const leafName =
      typeof obj['leafName'] === 'string' && obj['leafName']
        ? (obj['leafName'] as string)
        : typeof memberRaw['name'] === 'string'
          ? (leafTagSegment(memberRaw['name'] as string) ?? (memberRaw['name'] as string))
          : id
    const memberFullName =
      typeof memberRaw['name'] === 'string' ? (memberRaw['name'] as string) : leafName
    const folderIdx = memberFullName.lastIndexOf('/')
    const folder = folderIdx >= 0 ? memberFullName.slice(0, folderIdx) : ''
    // Read tag_path straight off the runtime :Tag node. Post-refactor
    // every UDT-member tag carries the canonical `[Provider]Folder/Tag`
    // form here; the legacy `[${providerName}]<composed>` fallback is
    // only used by graphs ingested before the udt-builders rewrite (no
    // provider/instance composition is honoured for those — those rows
    // would not have lived under a `:UDTInstance` HAS_MEMBER edge
    // either, so this query wouldn't return them anyway).
    const tagPath =
      typeof memberRaw['tag_path'] === 'string' && memberRaw['tag_path']
        ? (memberRaw['tag_path'] as string)
        : undefined
    const dataType =
      typeof memberRaw['data_type'] === 'string' && memberRaw['data_type']
        ? (memberRaw['data_type'] as string)
        : undefined
    const memberType =
      typeof memberRaw['member_type'] === 'string' && memberRaw['member_type']
        ? (memberRaw['member_type'] as string)
        : undefined
    out.push({
      id,
      _id: elementId,
      name: memberFullName,
      ignition_tag_type: 'UDTMember',
      ...(tagPath ? { tag_path: tagPath } : {}),
      ...(dataType ? { data_type: dataType } : {}),
      ...(memberType ? { member_type: memberType } : {}),
      ...(folder ? { folder } : {})
    })
  }
  // `context.providerName` / `context.instanceFullPath` are accepted
  // for backwards-compat with callers that pre-date the tag_path on
  // node refactor — they're no longer needed for path composition.
  void context
  return out
}

/**
 * Project the `{nestedInstance, memberName, definition, memberCount}`
 * rows from `PLANT_UDT_FOLDER_NODE_CYPHER`'s `directNestedInstances`
 * CALL into {@link PlantUdtNestedInstanceEntry}s. These are real
 * runtime `:UDTInstance` children of the current instance — rendered
 * as folder rows in the explorer. Clicking one pushes a
 * `udtNestedInstance` path segment carrying the nested instance's own
 * id so the next call walks straight into that instance's children
 * (where the override-merge bug used to live — see refactor commit
 * dropping per-instance override `:Tag` nodes).
 */
export function extractUdtNestedInstanceEntries(value: unknown): PlantUdtNestedInstanceEntry[] {
  if (!Array.isArray(value)) return []
  const out: PlantUdtNestedInstanceEntry[] = []
  const seen = new Set<string>()
  for (const item of value) {
    if (!item || typeof item !== 'object') continue
    const obj = item as Record<string, unknown>
    const nestedRaw = extractRawNode(obj['nestedInstance'])
    if (!nestedRaw) continue
    const nestedInstanceId =
      typeof nestedRaw['id'] === 'string'
        ? (nestedRaw['id'] as string)
        : typeof nestedRaw['_id'] === 'string'
          ? (nestedRaw['_id'] as string)
          : null
    if (!nestedInstanceId) continue
    if (seen.has(nestedInstanceId)) continue
    seen.add(nestedInstanceId)
    const memberName =
      typeof obj['memberName'] === 'string' && obj['memberName']
        ? (obj['memberName'] as string)
        : typeof nestedRaw['name'] === 'string'
          ? (leafTagSegment(nestedRaw['name'] as string) ?? (nestedRaw['name'] as string))
          : nestedInstanceId
    const defRaw = extractRawNode(obj['definition'])
    const nestedDefinitionId =
      defRaw && typeof defRaw['id'] === 'string'
        ? (defRaw['id'] as string)
        : defRaw && typeof defRaw['_id'] === 'string'
          ? (defRaw['_id'] as string)
          : null
    const nestedDefinitionName =
      defRaw && typeof defRaw['name'] === 'string'
        ? (defRaw['name'] as string)
        : typeof nestedRaw['type_id'] === 'string'
          ? (nestedRaw['type_id'] as string)
          : null
    out.push({
      id: nestedInstanceId,
      nestedInstanceId,
      memberName,
      nestedDefinitionId,
      nestedDefinitionName,
      memberCount: toIntCount(obj['memberCount'])
    })
  }
  return out
}

/**
 * Project the single row returned by `PLANT_UDT_FOLDER_NODE_CYPHER`
 * into the typed {@link PlantUdtFolderNodeResponse}: the direct
 * leaf members at the current `subPath`, the nested-instance folder
 * rows at the same level, and the one-level-deep subfolders.
 */
export function mapPlantUdtFolderNodeResponse(
  raw: CypherReadResult,
  args: PlantUdtFolderNodeArgs
): PlantUdtFolderNodeResponse {
  const row = raw.results[0] ?? {}
  const directMembers = extractUdtDirectMembers(row['directMembers'], {
    providerName: args.providerName,
    instanceFullPath: args.instanceFullPath
  })
  const directNestedInstances = extractUdtNestedInstanceEntries(row['directNestedInstances'])
  const subfolders = extractSubfolderEntries(row['subfolders'])
  return { directMembers, directNestedInstances, subfolders }
}
